import { useState } from "react";
import { CalendarIcon } from "lucide-react";
import { format, parse } from "date-fns";
import type { Matcher } from "react-day-picker";

import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toDateInputValue } from "@/lib/date-input";
import { cn } from "@/lib/utils";

interface DatePickerProps {
  /** Accepts an ISO `yyyy-MM-dd` or `MM/dd/yyyy` string; empty when unset. */
  value?: string;
  /** Receives an ISO `yyyy-MM-dd` string, or an empty string when cleared. */
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  /** Earliest selectable date, inclusive (ISO `yyyy-MM-dd`). Earlier days are disabled. */
  min?: string;
  /** Latest selectable date, inclusive (ISO `yyyy-MM-dd`). Later days are disabled. */
  max?: string;
  /**
   * Control height, matching `SelectTrigger`'s `size` API so date pickers and dropdowns line up
   * when placed side by side. `default` → h-9, `sm` → h-8. Height set here (not the base class) so
   * it can't be silently overridden the way `SelectTrigger`'s `data-[size]` height can.
   */
  size?: "sm" | "default";
  "aria-label"?: string;
}

/** Parse an ISO `yyyy-MM-dd` (or empty) bound to a Date, or undefined when unset/invalid. */
function parseBound(value: string | undefined): Date | undefined {
  if (!value) return undefined;
  const date = parse(value, "yyyy-MM-dd", new Date());
  return isNaN(date.getTime()) ? undefined : date;
}

/**
 * Shared date picker (popover + calendar) used across the platform so every
 * date input looks and behaves the same. Works in ISO `yyyy-MM-dd` and displays
 * `MM/dd/yyyy`.
 */
export function DatePicker({
  value,
  onChange,
  className,
  disabled,
  placeholder = "mm/dd/yyyy",
  min,
  max,
  size = "default",
  "aria-label": ariaLabel,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  const iso = toDateInputValue(value);
  const parsedDate = iso
    ? (() => {
        const d = parse(iso, "yyyy-MM-dd", new Date());
        return isNaN(d.getTime()) ? undefined : d;
      })()
    : undefined;

  const displayValue = parsedDate ? format(parsedDate, "MM/dd/yyyy") : "";

  const minDate = parseBound(min);
  const maxDate = parseBound(max);
  const disabledMatchers: Matcher[] = [];
  // `before`/`after` are exclusive of the boundary day, so min/max stay selectable.
  if (minDate) disabledMatchers.push({ before: minDate });
  if (maxDate) disabledMatchers.push({ after: maxDate });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            "inline-flex items-center justify-start gap-1.5 rounded-md border border-input bg-background px-3 text-sm shadow-xs transition-[color,box-shadow] hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
            size === "sm" ? "h-8" : "h-9",
            !displayValue && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="size-3.5 shrink-0" />
          <span className="truncate">{displayValue || placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[160]" align="start">
        <Calendar
          mode="single"
          selected={parsedDate}
          defaultMonth={parsedDate ?? minDate ?? maxDate}
          disabled={disabledMatchers.length > 0 ? disabledMatchers : undefined}
          onSelect={(date) => {
            onChange(date ? format(date, "yyyy-MM-dd") : "");
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
